<script setup lang="ts">
import CategoryBubble from './CategoryBubble.vue';
import { computed } from 'vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import type { Category } from '@ilteducation/content-ref-data-types';
import { useGrades } from './use-grades';
import { getThemeName } from './gui';
import { Video } from '../Results.vue';

const props = defineProps<{
  video: Video;
  subjectId: string;
}>();

const { byId: getAreaById } = useAreas();
const { byId: getCategoryById } = useCategories();
const { gradeAbbreviation } = useGrades();

const subject = computed(() => getCategoryById.value(props.subjectId));

// This assumes a video only exists in one area per market+subject combination...
const getSubjectVideoArea = computed(
  () => (subjectId: string) =>
    props.video.orderedAreas
      .flatMap((area) => area.id)
      .map(getAreaById.value)
      .find((a) => subjectId === a?.categoryId),
);
const area = computed(() => getSubjectVideoArea.value(props.subjectId));

const grades = computed(() =>
  props.video.categories
    .map((vc) => getCategoryById.value(vc))
    .filter((c) => c && c.kind === 'grade')
    .map((c) => gradeAbbreviation(c as Category)),
);
</script>

<template>
  <v-col v-if="props.video && subject && area" cols="6" sm="4" md="3">
    <v-sheet :theme="getThemeName(subject.slug)" class="single-subject-video mb-2">
      <router-link
        :to="{
          name: 'showFilm',
          params: {
            subject: subject.slug,
            area: area.slug,
            film: props.video.slug,
          },
        }"
        :aria-label="props.video.title"
      >
        <div class="image-subject-grade-wrapper pb-3">
          <v-img :src="props.video.thumbnail" alt="" :aspect-ratio="16 / 9">
            <template #placeholder>
              <v-skeleton-loader type="image" />
            </template>
          </v-img>
          <v-img class="play-overlay" src="@/assets/images/icon-play-circle.svg" alt="" />
          <div class="subject-grade d-flex flex-column gr-lg-1">
            <CategoryBubble v-if="subject.categoryCode" :subject="subject" size="small" />
            <div v-for="(grade, index) in grades" :key="index" class="grade bg-secondary-container">
              {{ grade }}
            </div>
          </div>
        </div>
        <div class="film-title underline text-h5">
          {{ props.video.title }}
        </div>
      </router-link>
    </v-sheet>
  </v-col>
</template>

<style lang="scss" scoped>
@use 'vuetify/settings';

.subject-grade {
  position: absolute;
  top: -8px;
  left: -10px;
  @media #{map-get(settings.$display-breakpoints, 'lg-and-up')} {
    top: 10px;
  }

  .grade {
    display: inline-block;
    border-radius: 100%;
    font-size: 0.75rem;
    line-height: 1.25rem;
    height: 1.75rem;
    width: 1.75rem;
    padding: 0.25rem 0.1875rem;
    text-align: center;
    text-transform: capitalize;

    @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
      font-weight: 500;
      line-height: 1.375rem;
      height: 2rem;
      width: 2rem;
      padding: 0.3125rem 0.1875rem;
    }
  }
}

.single-subject-video {
  background-color: transparent;
  a {
    text-decoration: none;
  }

  .image-subject-grade-wrapper {
    position: relative;

    img:first-of-type {
      border-radius: 4px;
      box-shadow: 0 4px 6px 0 rgba(18, 43, 64, 0.2);
    }

    .play-overlay {
      opacity: 0;
      position: absolute;
      width: 50px;
      height: 50px;
      margin: auto;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      filter: drop-shadow(1px 6px 12px rgba(18, 43, 64, 0.5));
      transition: opacity 0.3s ease-in-out;
    }

    &:hover {
      .play-overlay {
        opacity: 1;
      }
    }
  }
}

.film-title {
  font-size: 0.875rem !important;
  letter-spacing: 0.1px;
  @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
    font-size: 0.9375rem !important;
    letter-spacing: 0;
  }

  &.underline {
    text-decoration: none;
    border-bottom: 3px solid rgba(var(--v-theme-primary), 0.1);
    display: inline;
    line-height: 1.6;
    transition: all 0.1s ease;
  }
}
</style>
